import { useParams, useNavigate, Link } from 'react-router-dom';
import { useState, useEffect, useRef, useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/contexts/AuthContext';
import { PageHeader } from '@/components/layout';
import { Button, Card, CardContent, CardHeader, CardTitle, Spinner, Badge, Alert } from '@/components/ui';
import { VideoPlayer } from '@/components/video';
import type { VideoPlayerRef } from '@/components/video';
import {
  ClickablePhaseTimeline,
  PhaseDetailsPanel,
  TeachingVideoPlayer,
  FarSideNote,
  RefusedRuleNotes,
  ToleranceReviewPanel,
} from '@/components/template';
import { ChannelPicker } from '@/components/template/ChannelPicker';
import CalibrationTakesPanel from '@/components/template/CalibrationTakesPanel';
import ExamplePerformancesPanel from '@/components/template/ExamplePerformancesPanel';
import { useTemplate } from '@/hooks';
import { formatDate } from '@/utils/formatters';
import { extractYouTubeId } from '@/utils/youtube';
import { useMediaUrl } from '@/hooks/useMediaUrl';
import { finalizeTemplate, validateTemplate } from '@/services/phaseApi';
import type { TemplateValidationResult } from '@/services/phaseApi';
import { getErrorCode, getErrorMessage, getErrorSuggestion } from '@/utils/errors';
import type { AxiosError } from 'axios';
import { updateTemplateChannel } from '@/services/templateApi';
import { ValidationReport } from '@/components/template';
import { Dialog, DialogBackdrop, DialogPanel } from '@headlessui/react';
import { CheckCircleIcon, ClockIcon, PencilIcon, ExclamationTriangleIcon, PencilSquareIcon } from '@heroicons/react/24/outline';
import type { Annotation, Phase, Region } from '@/types/api';
import { normalizeTemplate, getTemplateMovementDescription, type NormalizedTemplate } from '@/utils/templateHelpers';
import { TemplateGuideOverlay } from '@/components/overlay/TemplateGuideOverlay';

interface TemplatePhase {
  phase_id?: string;
  label: string;
  phase_type?: string;
  start_frame: number;
  end_frame: number;
  importance?: string;
  description?: string;
  biomechanical_markers?: string[];
  timing_goal?: string;
}

/**
 * A template annotation as actually stored — a union of the canvas editor's
 * Annotation shape and older name/label/type variants, hence every field
 * being optional and loosely typed rather than asserting one canonical
 * shape. See the `regions` construction below (the sole consumer) for which
 * field-name aliases are read.
 */
interface RawStoredAnnotation {
  id?: string;
  description?: string;
  label?: string;
  name?: string;
  type?: Region['type'];
  landmark_indices?: number[];
  importance?: Region['importance'];
  target_phase?: string | null;
  activeFrames?: unknown;
  active_frames?: unknown;
  spatial_goal?: Annotation['regions'][number]['spatial_goal'];
  movement_goal?: Annotation['regions'][number]['movement_goal'];
}

/**
 * TemplateDetails - for phase-based templates (from /api/phases/templates)
 *
 * Features:
 * - Split view layout with video player and phase details panel
 * - Clickable phase timeline that seeks video
 * - Phase-specific annotations display
 * - Hull visualization per phase
 * - Phase importance display
 */
function TemplateDetails() {
  // 1. ALL HOOKS FIRST - must be called unconditionally in the same order every render
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const { data: template, isLoading, error, refetch } = useTemplate(id);
  const [annotations, setAnnotations] = useState<Annotation | null>(null);
  const [loadingAnnotations, setLoadingAnnotations] = useState(false);

  // Video player ref for seeking
  const videoPlayerRef = useRef<VideoPlayerRef>(null);
  const [currentFrame, setCurrentFrame] = useState(0);
  const [selectedPhase, setSelectedPhase] = useState<TemplatePhase | null>(null);

  // Finalization state
  const [isValidating, setIsValidating] = useState(false);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [showValidationReport, setShowValidationReport] = useState(false);
  const [validationResult, setValidationResult] = useState<TemplateValidationResult | null>(null);
  const [isFinalizing, setIsFinalizing] = useState(false);
  const [finalizationSuccess, setFinalizationSuccess] = useState(false);
  /*
   * NO auto-provisioned body-facing-hard-limit state here any more (PR-5,
   * owner decision D12: "I thought we were no longer using this?").
   *
   * Finalize used to materialise a body-facing hard limit by itself, with a
   * limit nobody typed, and this page announced it. That provisioning path is
   * deleted on the backend in PR-5, and a hard limit's failure angle now comes
   * only from the coach (in the studio) or from their own clips — so there is
   * nothing to announce. `finalizeTemplate` still RETURNS the field for wire
   * compatibility while the two halves ship; it reaches no UI, which the
   * retirement pin test in this folder keeps true.
   */
  /**
   * Structured calibration_incomplete 422 error — shown as a tier checklist
   * rather than a generic error string when the publish gate fires.
   */
  const [calibrationIncompleteError, setCalibrationIncompleteError] = useState<{
    message: string;
    missing_tiers: string[];
    tier_messages: Record<string, string>;
  } | null>(null);

  // Channel assignment state
  const [channelPickerOpen, setChannelPickerOpen] = useState(false);
  const [channelError, setChannelError] = useState<string | null>(null);

  // Normalize template to handle API inconsistencies.
  // useMemo is required here: normalizeTemplate returns a new object every call, so without
  // memoization the object reference changes every render. The useEffect below depends on
  // `normalized`, so a new reference each render would trigger the effect every render,
  // calling setAnnotations → re-render → new normalized → infinite loop that starves
  // React Router's Outlet from ever committing a route change.
  const normalized: NormalizedTemplate | null = useMemo(
    () => (template ? normalizeTemplate(template) : null),
    [template]
  );

  // Media-auth security sprint (2026-09-21): /videos/{id}/stream now requires
  // a bearer token or a signed sig/exp pair — a bare <video src> sends
  // neither. useMediaUrl must be called unconditionally (Rules of Hooks),
  // before the isLoading/!template early returns below.
  const { streamUrl: referenceVideoStreamUrl } = useMediaUrl(normalized?.referenceVideoId || undefined);

  // Convert normalized phases to TemplatePhase format (handles both label and name properties).
  // The raw phase can carry TemplatePhase's legacy field names (phase_id/label/
  // phase_type/etc.) in addition to Phase's canonical ones — both are read here.
  const phases: TemplatePhase[] = (normalized?.phases || []).map((phase: Phase & Partial<TemplatePhase>) => ({
    phase_id: phase.id || phase.phase_id,
    label: phase.label || phase.name || 'Unnamed Phase',
    phase_type: phase.phase_type || 'dynamic',
    start_frame: phase.start_frame,
    end_frame: phase.end_frame,
    importance: phase.importance,
    description: phase.description,
    biomechanical_markers: phase.biomechanical_markers,
    timing_goal: phase.timing_goal,
  }));

  // Extract annotations from template object (they're stored embedded, not in separate API)
  useEffect(() => {
    if (!normalized) {
      setAnnotations(null);
      setLoadingAnnotations(false);
      return;
    }

    setLoadingAnnotations(true);

    // Template stores annotations directly - transform them to Region format
    const templateAnnotations = normalized.annotations || [];

    if (templateAnnotations.length > 0) {
      // `normalized.annotations` is deliberately `unknown[]` — templates store
      // annotations in more than one historical shape (canvas editor's
      // Annotation, an older name/label/type variant), so this defensively
      // reads every field-name alias actually seen in the wild rather than
      // asserting one shape. `activeFrames` in particular is passed through
      // as-is without reshaping (pre-existing behavior, unchanged here) even
      // though the source shapes for it aren't identical to Region's —
      // hence the cast at the assignment below rather than a stronger type.
      const regions: Annotation['regions'] = (templateAnnotations as RawStoredAnnotation[]).map((ann) => ({
        id: ann.id || crypto.randomUUID(),
        name: ann.description || ann.label || ann.name || 'Body Region',
        type: ann.type || 'polygon',
        landmark_indices: ann.landmark_indices || [],
        importance: ann.importance,
        target_phase: ann.target_phase,
        activeFrames: (ann.activeFrames ?? ann.active_frames) as Annotation['regions'][number]['activeFrames'],
        spatial_goal: ann.spatial_goal,
        movement_goal: ann.movement_goal,
      }));

      setAnnotations({ video_id: normalized.referenceVideoId || '', regions });
    } else {
      setAnnotations(null);
    }

    setLoadingAnnotations(false);
  }, [normalized]);

  // Handle phase click - seek video and show phase panel
  const handlePhaseClick = (phase: TemplatePhase) => {
    setSelectedPhase(phase);
    videoPlayerRef.current?.seekToFrame(phase.start_frame);
  };

  // Handle video time update
  const handleFrameChange = (frame: number) => {
    setCurrentFrame(frame);
  };

  // Play phase segment
  const handlePlayPhase = (startFrame: number, _endFrame: number) => {
    videoPlayerRef.current?.seekToFrame(startFrame);
    videoPlayerRef.current?.play();
    // Note: Auto-pause at _endFrame would require additional logic
  };

  // Validation and finalization handlers
  const handleFinalizeClick = async () => {
    if (!normalized?.id) return;

    setIsValidating(true);
    setFinalizationSuccess(false);
    setValidationErrors([]);
    setCalibrationIncompleteError(null);

    try {
      const result = await validateTemplate(normalized.id);
      setValidationResult(result);
      setShowValidationReport(true);
    } catch (error) {
      console.error('Validation request failed:', error);
      setValidationErrors([
        error instanceof Error ? error.message : 'Could not run validation. Please try again.',
      ]);
    } finally {
      setIsValidating(false);
    }
  };

  const handleConfirmFinalize = async () => {
    if (!normalized?.id) return;

    setIsFinalizing(true);
    setCalibrationIncompleteError(null);
    try {
      await finalizeTemplate(normalized.id, 'Coach', true);
      setFinalizationSuccess(true);
      setShowValidationReport(false);
      setValidationResult(null);
      setValidationErrors([]);
      // Invalidate React Query cache to force fresh data
      await queryClient.invalidateQueries({ queryKey: ['templates', normalized.id] });
      await queryClient.invalidateQueries({ queryKey: ['templates'] });
      // Refetch template data to get updated finalization status
      await refetch();
    } catch (error) {
      console.error('Failed to finalize template:', error);
      setShowValidationReport(false);

      const code = getErrorCode(error);
      if (code === 'calibration_incomplete') {
        // Extract structured tier info from the preserved `detail` object
        const axErr = error as AxiosError<{ detail?: { missing_tiers?: string[]; tier_messages?: Record<string, string>; message?: string } }>;
        const detail = axErr.response?.data?.detail;
        const structuredDetail = typeof detail === 'object' && detail !== null ? detail : {};
        setCalibrationIncompleteError({
          message:
            getErrorMessage(error, 'This template requires calibration takes in all three tiers before it can be published.'),
          missing_tiers: (structuredDetail as { missing_tiers?: string[] }).missing_tiers ?? [],
          tier_messages: (structuredDetail as { tier_messages?: Record<string, string> }).tier_messages ?? {},
        });
      } else {
        const msg = getErrorMessage(error, 'Failed to finalize template. Please try again.');
        const suggestion = getErrorSuggestion(error);
        setValidationErrors(suggestion ? [msg, suggestion] : [msg]);
      }
    } finally {
      setIsFinalizing(false);
    }
  };

  const handleEditTemplate = () => {
    if (!normalized?.id) return;
    navigate(`/templates/${normalized.id}/edit`);
  };

  // Channel assignment handler — calls PATCH /api/templates/{id}/channel
  const handleChannelChange = async (channelId: string | null) => {
    if (!normalized?.id) return;
    try {
      await updateTemplateChannel(normalized.id, channelId);
      setChannelPickerOpen(false);
      setChannelError(null);
      // Refresh both the detail and the list so TemplateLibrary shows the updated channel
      queryClient.invalidateQueries({ queryKey: ['templates'] });
      await refetch();
    } catch {
      // 403 if non-owner; surface a recoverable inline error
      setChannelError('Could not update channel. You may not own this template.');
    }
  };

  // 2. CONDITIONAL RETURNS AFTER ALL HOOKS
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Spinner size="lg" label="Loading template..." />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-danger font-semibold">Error loading template</p>
        <p className="text-text-secondary mt-2">
          {error instanceof Error ? error.message : 'Failed to fetch template data'}
        </p>
        <p className="text-xs text-text-muted mt-1">Template ID: {id}</p>
        <Button onClick={() => navigate('/templates')} className="mt-4">
          Back to Templates
        </Button>
      </div>
    );
  }

  if (!template || !normalized) {
    return (
      <div className="text-center py-12">
        <p className="text-text-secondary">Template not found</p>
        <p className="text-xs text-text-muted mt-1">Template ID: {id}</p>
        <Button onClick={() => navigate('/templates')} className="mt-4">
          Back to Templates
        </Button>
      </div>
    );
  }

  // Get selected phase ID for timeline highlighting
  const selectedPhaseId = selectedPhase?.phase_id || (selectedPhase ? `phase-${phases.indexOf(selectedPhase)}` : null);

  // Filter regions for selected phase
  // Show regions where target_phase matches the phase label OR target_phase is null/undefined (global regions)
  const phaseFilteredRegions = selectedPhase && annotations?.regions
    ? annotations.regions.filter(
        (region) => !region.target_phase || region.target_phase === selectedPhase.label
      )
    : annotations?.regions || [];

  return (
    <div>
      <PageHeader
        title={normalized.name}
        description={getTemplateMovementDescription(normalized)}
        showBack
        action={
          <div className="flex items-center gap-3">
            {/* Status Badge */}
            {normalized.finalized ? (
              <Badge variant="success" size="lg" className="flex items-center gap-1.5">
                <CheckCircleIcon className="h-4 w-4" />
                Finalized
              </Badge>
            ) : (
              <Badge variant="warning" size="lg" className="flex items-center gap-1.5">
                <ClockIcon className="h-4 w-4" />
                Draft
              </Badge>
            )}

            {/* Archived badge (D12) */}
            {template?.is_archived && (
              <Badge variant="secondary" size="lg" title="Archived — excluded from your active-template limit">
                Archived
              </Badge>
            )}

            {/* Edit Template Button — always visible */}
            <Button onClick={handleEditTemplate} variant="outline">
              <PencilIcon className="h-5 w-5 mr-2" />
              Edit Template
            </Button>

            {/* Finalization Action Button — only for drafts */}
            {!normalized.finalized && (
              <Button
                onClick={handleFinalizeClick}
                disabled={isValidating}
                className="bg-accent hover:bg-accent-hover"
              >
                {isValidating ? (
                  <>
                    <Spinner size="sm" className="mr-2" />
                    Checking...
                  </>
                ) : (
                  <>
                    <CheckCircleIcon className="h-5 w-5 mr-2" />
                    Finalize Template
                  </>
                )}
              </Button>
            )}

            {/* Evaluate Athlete Button */}
            <Button onClick={() => navigate(`/evaluate/${normalized.id}`)}>
              Evaluate Athlete
            </Button>
          </div>
        }
      />

      {/* Adaptive Guide Overlay — calibration-upload-guidance. No auto-open-on-mount
          here (unlike CreateTemplate's StudioGuideOverlay): TemplateDetails is a
          multi-purpose page (phases, video, channel, finalize, evaluate) and this
          guide is scoped to the Calibration Takes panel specifically, so popping it
          open on every visit — including visits that have nothing to do with
          calibration — would be page-inappropriate noise. Discoverability instead
          comes from the always-visible short inline copy in CalibrationTakesPanel
          plus its "Guide" button and "Learn more" link, both of which call
          setGuideOpen(true) on demand. */}
      <TemplateGuideOverlay />

      {/* Success Message */}
      {finalizationSuccess && (
        <Alert variant="success" className="mb-6" onClose={() => setFinalizationSuccess(false)}>
          <strong>Template finalized successfully!</strong> This template is now ready for athlete evaluations.
          {normalized.finalizedAt && (
            <p className="mt-1 text-sm">
              Finalized on {formatDate(normalized.finalizedAt)}
              {normalized.finalizedBy && ` by ${normalized.finalizedBy}`}
            </p>
          )}
        </Alert>
      )}

      {/* Coach-only template notices (viewpoint observability Phase 1).
          Both render nothing on the common path — far_side_note and
          refused_rule_texts are writer-only metadata keys that are absent
          unless finalize computed them.

          THE one refusal surface (PR-8, audit items RC-13 / FE-12). The same
          refusal used to render here AND as the rationale on a not_expressible
          compile card further down this same page, in two different wordings.
          This list owns it: `refused_rule_details` preferred for the reason
          beside the coach's phrase, `refused_rule_texts` the fallback for a
          template finalized before PR-8. */}
      <FarSideNote entries={normalized.metadata?.far_side_note} className="mb-6" />
      <RefusedRuleNotes
        details={normalized.metadata?.refused_rule_details}
        texts={normalized.metadata?.refused_rule_texts}
        className="mb-6"
      />
      {/* Tolerance review (observability Phase 4) — which angle checks still
          run the standard strictness. Same coach-only shape: the source key is
          writer-only, and the panel renders nothing when nothing is left on
          the standard setting. */}
      <ToleranceReviewPanel
        review={normalized.metadata?.tolerance_review}
        onReview={handleEditTemplate}
        className="mb-6"
      />

      {/* Publish gate: calibration_incomplete — per-tier checklist */}
      {calibrationIncompleteError && (
        <Alert
          variant="error"
          className="mb-6"
          onClose={() => setCalibrationIncompleteError(null)}
          data-testid="calibration-incomplete-error"
        >
          <h4 className="font-semibold mb-2 flex items-center gap-2">
            <ExclamationTriangleIcon className="h-5 w-5" />
            Before publishing, this template needs:
          </h4>
          <ul className="space-y-2 text-sm">
            {Object.entries(calibrationIncompleteError.tier_messages).map(([tier, msg]) => (
              <li key={tier} className="flex items-start gap-2">
                <span className="mt-0.5 shrink-0 text-danger">&#x25CF;</span>
                <span>{msg}</span>
              </li>
            ))}
          </ul>
          {calibrationIncompleteError.missing_tiers.length > 0 &&
            Object.keys(calibrationIncompleteError.tier_messages).length === 0 && (
              <p className="text-sm mt-1">{calibrationIncompleteError.message}</p>
            )}
        </Alert>
      )}

      {/* Validation Errors */}
      {validationErrors.length > 0 && (
        <Alert variant="error" className="mb-6" onClose={() => setValidationErrors([])}>
          <h4 className="font-semibold mb-2 flex items-center gap-2">
            <ExclamationTriangleIcon className="h-5 w-5" />
            Cannot finalize template:
          </h4>
          <ul className="list-disc list-inside space-y-1 text-sm">
            {validationErrors.map((error, idx) => (
              <li key={idx}>{error}</li>
            ))}
          </ul>
        </Alert>
      )}

      {/* Phase Timeline - Full Width */}
      {phases.length > 0 && (
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>Phase Timeline</CardTitle>
          </CardHeader>
          <CardContent>
            <ClickablePhaseTimeline
              phases={phases}
              totalFrames={normalized.totalFrames}
              currentFrame={currentFrame}
              selectedPhaseId={selectedPhaseId}
              onPhaseClick={handlePhaseClick}
              fps={normalized.fps}
            />
          </CardContent>
        </Card>
      )}

      {/* Split View - Video Player and Phase Details */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* Video Player */}
        <Card>
          <CardHeader>
            <CardTitle>Reference Video</CardTitle>
          </CardHeader>
          <CardContent>
            {normalized.referenceVideoId && referenceVideoStreamUrl ? (
              <div className="space-y-4">
                <VideoPlayer
                  ref={videoPlayerRef}
                  src={referenceVideoStreamUrl}
                  fps={normalized.fps}
                  showControls={true}
                  onFrameChange={handleFrameChange}
                />

                {/* Video metadata */}
                <div className="grid grid-cols-3 gap-4 text-sm">
                  <div>
                    <span className="text-text-secondary">FPS:</span>
                    <span className="ml-2 font-medium text-text-primary">{normalized.fps}</span>
                  </div>
                  <div>
                    <span className="text-text-secondary">Frames:</span>
                    <span className="ml-2 font-medium text-text-primary">{normalized.totalFrames}</span>
                  </div>
                  <div>
                    <span className="text-text-secondary">Duration:</span>
                    <span className="ml-2 font-medium text-text-primary">{normalized.duration.toFixed(2)}s</span>
                  </div>
                </div>
              </div>
            ) : (
              <div className="aspect-video bg-dark-elevated-1 rounded flex items-center justify-center">
                <p className="text-text-secondary">
                  {normalized.referenceVideoId ? 'Loading video…' : 'No video available'}
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* YouTube reference video (YTI-2) — embedded when the coach attached one */}
        {(() => {
          const ytId = extractYouTubeId(template?.youtube_url ?? null);
          if (!ytId) return null;
          return (
            <Card>
              <CardHeader>
                <CardTitle>YouTube Reference</CardTitle>
              </CardHeader>
              <CardContent>
                <iframe
                  title="YouTube reference video"
                  src={`https://www.youtube-nocookie.com/embed/${ytId}`}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  className="w-full aspect-video rounded border border-border-subtle"
                />
              </CardContent>
            </Card>
          );
        })()}

        {/* Phase Details Panel */}
        <Card>
          <CardHeader>
            <CardTitle>Phase Details</CardTitle>
          </CardHeader>
          <CardContent>
            <PhaseDetailsPanel
              phase={selectedPhase}
              fps={normalized.fps}
              regions={phaseFilteredRegions}
              onPlayPhase={handlePlayPhase}
              onClose={() => setSelectedPhase(null)}
            />
          </CardContent>
        </Card>
      </div>

      {template?.teaching_video_id && (
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>Coach Instruction</CardTitle>
          </CardHeader>
          <CardContent>
            <TeachingVideoPlayer
              teachingVideoId={template.teaching_video_id}
              sourceType={template.teaching_video_source_type ?? 'video'}
            />
          </CardContent>
        </Card>
      )}

      {/* Example performances — athlete-facing, visible to everyone with
          template read access (backlog/example-performances-template-
          resources.md, PR 1+2). Self-gates its own empty state: renders
          nothing for an athlete when no takes are exposed, a quiet hint for
          a coach. */}
      {normalized.id && (
        <div className="mb-6">
          <ExamplePerformancesPanel
            templateId={normalized.id}
            isCoach={user?.role === 'coach' || user?.role === 'admin'}
          />
        </div>
      )}

      {/* Calibration Takes panel — only for template owners/writers (coaches and admins).
          Guards: role must be 'coach' or 'admin' to manage takes.
          Athletes can view evaluations but never see this panel. */}
      {(user?.role === 'coach' || user?.role === 'admin') && normalized.id && (
        <div className="mb-6">
          <CalibrationTakesPanel templateId={normalized.id} />
        </div>
      )}

      {/* Template Info and Regions - Below the split view */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Template Details */}
        <Card>
          <CardHeader>
            <CardTitle>Details</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {/* Channel assignment row */}
            <div>
              <span className="text-sm text-text-secondary">Channel</span>
              <div className="flex items-center gap-2 mt-0.5">
                {template?.channel_slug ? (
                  <Link
                    to={`/channels/${template.channel_slug}`}
                    className="text-sm font-medium text-accent hover:underline"
                  >
                    {template.channel_slug}
                  </Link>
                ) : (
                  <span className="text-sm text-text-muted">Not assigned</span>
                )}
                {/* Only coaches and admins can change the channel */}
                {(user?.role === 'coach' || user?.role === 'admin') && (
                  <button
                    onClick={() => setChannelPickerOpen(true)}
                    className="ml-1 p-1 rounded hover:bg-dark-elevated-2"
                    aria-label="Change channel"
                  >
                    <PencilSquareIcon className="h-4 w-4 text-text-muted" />
                  </button>
                )}
              </div>
              {channelError && (
                <p className="text-xs text-danger mt-1">{channelError}</p>
              )}
            </div>

            {normalized.sportType && (
              <div>
                <span className="text-sm text-text-secondary">Sport Type</span>
                <p className="font-medium text-text-primary">{normalized.sportType}</p>
              </div>
            )}
            <div>
              <span className="text-sm text-text-secondary">Movement</span>
              <p className="font-medium text-text-primary">{normalized.movementType}</p>
            </div>
            {normalized.createdAt && (
              <div>
                <span className="text-sm text-text-secondary">Created</span>
                <p className="font-medium text-text-primary">{formatDate(normalized.createdAt)}</p>
              </div>
            )}
            <div>
              <span className="text-sm text-text-secondary">Total Phases</span>
              <p className="font-medium text-text-primary">{phases.length}</p>
            </div>
            <div>
              <span className="text-sm text-text-secondary">Total Frames</span>
              <p className="font-medium text-text-primary">{normalized.totalFrames}</p>
            </div>
            <div>
              <span className="text-sm text-text-secondary">Duration</span>
              <p className="font-medium text-text-primary">{normalized.duration.toFixed(2)}s</p>
            </div>
            {normalized.finalized && normalized.finalizedAt && (
              <>
                <div className="pt-3 border-t border-border-subtle">
                  <span className="text-sm text-text-secondary">Finalized On</span>
                  <p className="font-medium text-text-primary">{formatDate(normalized.finalizedAt)}</p>
                </div>
                {normalized.finalizedBy && (
                  <div>
                    <span className="text-sm text-text-secondary">Finalized By</span>
                    <p className="font-medium text-text-primary">{normalized.finalizedBy}</p>
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>

        {/* Phases List */}
        <Card>
          <CardHeader>
            <CardTitle>All Phases</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2 max-h-80 overflow-y-auto">
              {phases.length > 0 ? (
                phases.map((phase: TemplatePhase, index: number) => (
                  <button
                    key={phase.phase_id || index}
                    className={`w-full text-left p-3 rounded border transition-colors ${
                      selectedPhaseId === (phase.phase_id || `phase-${index}`)
                        ? 'bg-info/10 border-info'
                        : 'bg-dark-elevated-1 border-border-subtle hover:bg-dark-elevated-2'
                    }`}
                    onClick={() => handlePhaseClick(phase)}
                  >
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm font-semibold">{phase.label}</span>
                      <Badge variant="secondary" size="sm">
                        {phase.phase_type || 'unknown'}
                      </Badge>
                    </div>
                    <div className="text-xs text-text-secondary space-y-0.5">
                      <p>Frames: {phase.start_frame} - {phase.end_frame}</p>
                      <p>Duration: {((phase.end_frame - phase.start_frame) / normalized.fps).toFixed(2)}s</p>
                      {phase.importance && <p>Importance: {phase.importance}</p>}
                    </div>
                  </button>
                ))
              ) : (
                <p className="text-sm text-text-secondary">No phases defined</p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Tracked Regions */}
        <Card>
          <CardHeader>
            <CardTitle>Tracked Regions</CardTitle>
          </CardHeader>
          <CardContent>
            {loadingAnnotations ? (
              <div className="flex items-center justify-center py-8">
                <Spinner size="sm" label="Loading annotations..." />
              </div>
            ) : annotations && annotations.regions && annotations.regions.length > 0 ? (
              <div className="space-y-2 max-h-80 overflow-y-auto">
                {annotations.regions.map((region, index) => (
                  <div
                    key={region.id || index}
                    className="p-3 border border-border-subtle rounded-lg bg-dark-elevated-1"
                  >
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-medium text-sm text-text-primary">{region.name}</span>
                      <Badge variant="secondary" size="sm">
                        {region.type}
                      </Badge>
                    </div>
                    <div className="text-xs text-text-secondary">
                      <p>Landmarks: {region.landmark_indices?.length || 0} points</p>
                      {region.landmark_indices && region.landmark_indices.length > 0 && (
                        <p className="font-mono truncate">
                          [{region.landmark_indices.slice(0, 8).join(', ')}
                          {region.landmark_indices.length > 8 && '...'}]
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-text-secondary">
                <p>No regions defined for this template</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Channel Picker Modal */}
      <ChannelPicker
        isOpen={channelPickerOpen}
        onClose={() => setChannelPickerOpen(false)}
        currentChannelId={template?.channel_id ?? null}
        onSelect={handleChannelChange}
      />

      {/* Validation Report Modal */}
      <Dialog
        open={showValidationReport}
        onClose={() => !isFinalizing && setShowValidationReport(false)}
        className="relative z-50"
      >
        <DialogBackdrop
          transition
          className="fixed inset-0 bg-black/60 backdrop-blur-sm duration-300 ease-out data-[closed]:opacity-0"
        />
        <div className="fixed inset-0 flex items-center justify-center p-4">
          <DialogPanel
            transition
            className="w-full max-w-2xl max-h-[85vh] overflow-y-auto rounded-xl bg-dark-card border border-border-subtle shadow-modal duration-300 ease-out data-[closed]:scale-95 data-[closed]:opacity-0"
          >
            {validationResult && (
              <ValidationReport
                result={validationResult}
                onAccept={handleConfirmFinalize}
                onGoBack={() => !isFinalizing && setShowValidationReport(false)}
                isAccepting={isFinalizing}
                farSideNote={normalized.metadata?.far_side_note}
              />
            )}
          </DialogPanel>
        </div>
      </Dialog>
    </div>
  );
}

export default TemplateDetails;
